import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";

const HeaderContainer = styled.header`
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const TopBar = styled.div`
  background: #dc2626;
  color: white;
  padding: 10px 0;
  font-size: 14px;

  .container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .contact-info {
    display: flex;
    align-items: center;
    gap: 2rem;

    a {
      color: white;
      display: flex;
      align-items: center;
      gap: 0.5rem;

      &:hover {
        color: #fecaca;
      }
    }
  }

  @media (max-width: 768px) {
    .contact-info {
      gap: 1rem;
      font-size: 12px;
    }
  }
`;

const MainHeader = styled.div`
  padding: 1rem 0;

  .container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .logo {
    img {
      height: 60px;
      width: auto;
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 2rem;

    .phone-section {
      display: flex;
      align-items: center;
      gap: 1rem;
      color: #dc2626;
      font-weight: 600;
      font-size: 1.1rem;

      img {
        width: 20px;
        height: 20px;
      }
    }

    .quote-btn {
      padding: 10px 25px;
      background: #dc2626;
      color: white;
      border-radius: 25px;
      font-weight: 500;
      transition: all 0.3s ease;

      &:hover {
        background: #b91c1c;
        transform: translateY(-2px);
      }
    }
  }

  @media (max-width: 768px) {
    .header-right {
      gap: 1rem;

      .phone-section {
        font-size: 0.9rem;
      }

      .quote-btn {
        padding: 8px 15px;
        font-size: 0.9rem;
      }
    }
  }
`;

const Navigation = styled.nav`
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;

  .nav-container {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
  }

  .nav-menu {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      position: relative;

      &:hover .dropdown {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
      }
    }

    a {
      display: block;
      padding: 1rem 1.5rem;
      color: #333;
      font-weight: 500;
      transition: all 0.3s ease;

      &:hover,
      &.active {
        color: #dc2626;
        background: rgba(220, 38, 38, 0.1);
      }
    }
  }

  .dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    background: white;
    min-width: 250px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    opacity: 0;
    visibility: hidden;
    transform: translateY(-10px);
    transition: all 0.3s ease;
    z-index: 1000;

    a {
      padding: 0.75rem 1.5rem;
      border-bottom: 1px solid #f0f0f0;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background: #f8f9fa;
      }
    }
  }

  .mobile-menu-button {
    display: none;
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #333;
    cursor: pointer;
    padding: 1rem;

    @media (max-width: 768px) {
      display: block;
    }
  }

  .mobile-menu {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    z-index: 1000;

    &.open {
      display: block;
    }

    .nav-menu {
      flex-direction: column;

      li {
        border-bottom: 1px solid #f0f0f0;

        &:last-child {
          border-bottom: none;
        }
      }

      a {
        padding: 1rem;
      }

      .dropdown {
        position: static;
        opacity: 1;
        visibility: visible;
        transform: none;
        box-shadow: none;
        background: #f8f9fa;

        a {
          padding-left: 2rem;
        }
      }
    }
  }

  @media (max-width: 768px) {
    .nav-menu {
      display: none;
    }
  }
`;

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <HeaderContainer>
      <TopBar>
        <div className="container">
          <div className="contact-info">
            <a href="tel:0413960242">
              <img
                src="/images/phn_icon_whte.png"
                alt="Phone"
              />
              0413960242
            </a>
            <a href="mailto:contact@ausgriptyres.com.au">
              <img
                src="/images/email_icon.png"
                alt="Email"
              />
              contact@ausgriptyres.com.au
            </a>
          </div>
        </div>
      </TopBar>

      <MainHeader>
        <div className="container">
          <div className="logo">
            <Link to="/">
              <img
                src="/images/logo.png"
                alt="Ausgrip Tyres"
              />
            </Link>
          </div>
          <div className="header-right">
            <div className="phone-section">
              <img
                src="/images/phn_icon_red.png"
                alt="Phone"
              />
              <a href="tel:0413960242">0413960242</a>
            </div>
            <Link
              to="/get-quote"
              className="quote-btn">
              Get a Quote
            </Link>
          </div>
        </div>
      </MainHeader>

      <Navigation>
        <div className="container">
          <div className="nav-container">
            <button
              className="mobile-menu-button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              ☰
            </button>

            <ul className="nav-menu">
              <li>
                <Link
                  to="/"
                  className={isActive("/") ? "active" : ""}>
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className={isActive("/about") ? "active" : ""}>
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  className={isActive("/services") ? "active" : ""}>
                  Services
                </Link>
                <div className="dropdown">
                  <Link to="/services#mobile-fitting">Mobile Tyre Fitting</Link>
                  <Link to="/services#new-tyres">Reliable New Tyres</Link>
                  <Link to="/services#puncture-repair">Tyre Puncture Repair</Link>
                  <Link to="/services#tyre-rotation">Tyre Rotation</Link>
                </div>
              </li>
              <li>
                <a
                  href="#"
                  onClick={(e) => e.preventDefault()}>
                  Tyres
                </a>
                <div className="dropdown">
                  <Link to="/landlux-tyres">Landlux Tyres</Link>
                </div>
              </li>
              <li>
                <Link
                  to="/trailer-repair"
                  className={isActive("/trailer-repair") ? "active" : ""}>
                  Trailer tyre repair
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className={isActive("/contact") ? "active" : ""}>
                  Contact Us
                </Link>
              </li>
            </ul>

            <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
              <ul className="nav-menu">
                <li>
                  <Link
                    to="/"
                    onClick={() => setMobileMenuOpen(false)}>
                    Home
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    onClick={() => setMobileMenuOpen(false)}>
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services"
                    onClick={() => setMobileMenuOpen(false)}>
                    Services
                  </Link>
                  <div className="dropdown">
                    <Link
                      to="/services#mobile-fitting"
                      onClick={() => setMobileMenuOpen(false)}>
                      Mobile Tyre Fitting
                    </Link>
                    <Link
                      to="/services#new-tyres"
                      onClick={() => setMobileMenuOpen(false)}>
                      Reliable New Tyres
                    </Link>
                    <Link
                      to="/services#new-tyres"
                      onClick={() => setMobileMenuOpen(false)}>
                      Reliable New Tyres
                    </Link>
                    <Link
                      to="/services#puncture-repair"
                      onClick={() => setMobileMenuOpen(false)}>
                      Tyre Puncture Repair
                    </Link>
                    <Link
                      to="/services#tyre-rotation"
                      onClick={() => setMobileMenuOpen(false)}>
                      Tyre Rotation
                    </Link>
                  </div>
                </li>
                <li>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setMobileMenuOpen(false);
                    }}>
                    Tyres
                  </a>
                  <div className="dropdown">
                    <Link
                      to="/landlux-tyres"
                      onClick={() => setMobileMenuOpen(false)}>
                      Landlux Tyres
                    </Link>
                  </div>
                </li>
                <li>
                  <Link
                    to="/trailer-repair"
                    onClick={() => setMobileMenuOpen(false)}>
                    Trailer tyre repair
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    onClick={() => setMobileMenuOpen(false)}>
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Navigation>
    </HeaderContainer>
  );
};

export default Header;
